/**
 * Printing Process Utilities
 *
 * Utilities for managing printing process preferences and ordering.
 */

import type { AdaptiveProcess, Process } from '~~/layers/base/types/printingProcess'

export interface PreferredPrintingProcess {
    name: string
    processId: number
    shortName: string
}

/**
 * Preferred order for printing processes.
 * Processes are sorted according to this order when displayed.
 */
export const PREFERRED_PRINTING_PROCESSES: PreferredPrintingProcess[] = [
    { name: 'Thermography', processId: 1, shortName: 't' },
    { name: 'Letterpress', processId: 5, shortName: 'l' },
    { name: 'Engraving', processId: 2, shortName: 'e' },
    { name: 'Digital', processId: 21, shortName: 'g' },
]

export const PRINTING_PROCESS_NAME_BY_ID_MAP: Record<number, string> = {
    2: 'Engraving',
    5: 'Letterpress',
    1: 'Thermography',
    21: 'Digital',
}

export const PRINTING_PROCESS_SHORTNAME_BY_ID_MAP: Record<number, string> = {
    1: 't',
    5: 'l',
    2: 'e',
    21: 'g',
}

/**
 * Gets the shortName for a printing process with fallback to processId mapping.
 * If shortName is missing, attempts to look it up from PRINTING_PROCESS_SHORTNAME_BY_ID_MAP using processId.
 *
 * @param print - The printing process object (Print type or object with processId/shortName)
 * @returns The shortName string, or undefined if not found
 */
export function getPrintingProcessShortName(
    print: { shortName?: string; processId?: number } | null | undefined
): string | undefined {
    if (!print) return undefined

    // First try to use the shortName directly
    if (print.shortName) {
        return print.shortName
    }

    // Fallback to processId mapping if shortName is missing
    if (print.processId) {
        return PRINTING_PROCESS_SHORTNAME_BY_ID_MAP[print.processId]
    }

    return undefined
}

/**
 * Checks if the current date and time is within the acceptable frame for showing warnings.
 * Warnings should be shown if the current time is after the start date (if provided)
 * and before the end date (if provided).
 *
 * @param process - Process or AdaptiveProcess; Drawer passes flattened AdaptiveProcess to Detail.
 * @returns true if warnings should be shown, false otherwise
 */
export function shouldShowWarnings(process: Process | AdaptiveProcess): boolean {
    const now = new Date()

    // If start date is provided, check if current time is after it
    if (process.warningStartDate) {
        const startDate = new Date(process.warningStartDate)
        if (isNaN(startDate.getTime())) {
            // Invalid date string, don't show warnings
            return false
        }
        if (now < startDate) {
            // Current time is before start date
            return false
        }
    }

    // If end date is provided, check if current time is before it
    if (process.warningEndDate) {
        const endDate = new Date(process.warningEndDate)
        if (isNaN(endDate.getTime())) {
            // Invalid date string, don't show warnings
            return false
        }
        if (now >= endDate) {
            // Current time is at or after end date
            return false
        }
    }

    // Current time is within the acceptable frame (or no dates are provided)
    return true
}
